import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { browser } from '$app/environment';
import { DEFAULT_ROLE_PERMISSIONS, resolveBaseTier } from '$lib/types';
import type { RolePermissions, RolePermissionsConfig } from '$lib/types';
import type { Staff } from '../../../../shared/src/schemas/staff';
import { lookups } from './lookups.svelte';
import { userState } from './auth.svelte';

let _config = $state<RolePermissionsConfig | null>(null);
let _unsubscribe: (() => void) | null = null;

/**
 * Impersonation lasts until "Stop impersonating", not until the next reload.
 *
 * It used to live only in memory, so a full page load -- a pasted URL, a
 * refresh, a link that reloaded -- quietly dropped you back to yourself, and
 * "what does this teacher see on that page?" could not be asked by going to
 * that page. Session storage keeps it to this tab and ends it with the tab.
 *
 * Restored at module load rather than once lookups arrive, so the first render
 * after a reload is already the impersonated view instead of a flash of the
 * administrator's own data. Nothing here widens access: Firestore rules apply
 * to the signed-in token whoever the screen is drawn for.
 */
const IMPERSONATION_KEY = 'updraft:impersonating';

function restoreImpersonation(): Staff | null {
	if (!browser) return null;
	try {
		const saved = sessionStorage.getItem(IMPERSONATION_KEY);
		return saved ? (JSON.parse(saved) as Staff) : null;
	} catch {
		return null;
	}
}

function rememberImpersonation(staff: Staff | null) {
	if (!browser) return;
	try {
		if (staff) sessionStorage.setItem(IMPERSONATION_KEY, JSON.stringify(staff));
		else sessionStorage.removeItem(IMPERSONATION_KEY);
	} catch {
		// Storage unavailable: impersonation still works, it just will not survive a reload.
	}
}

let _impersonatedStaff = $state<Staff | null>(restoreImpersonation());

/** The staff member currently being impersonated, or null. */
export const impersonationState = {
	get staff() {
		return _impersonatedStaff;
	}
};

/** Begin impersonating a staff member. Admin-only — never call from non-admin contexts. */
export function startImpersonating(staff: Staff) {
	_impersonatedStaff = staff;
	rememberImpersonation(staff);
	recompute();
}

/** Stop impersonating and restore the real user's permissions. */
export function stopImpersonating() {
	_impersonatedStaff = null;
	rememberImpersonation(null);
	recompute();
}

/**
 * Reactive permissions state for the current user.
 * Components: {#if rolePermissions.behavior} ... {/if}
 * Loading guard: {#if rolePermissions.loading} ... {/if}
 */
export const rolePermissions = $state<RolePermissions & { loading: boolean }>({
	loading: true,
	...DEFAULT_ROLE_PERMISSIONS['_default']
});

/**
 * How the current tier was resolved — surfaced in the Footer so "why can't I
 * see behavior data?" is answerable by looking at the bottom of the screen.
 */
export const rolePermissionsDebug = $state({
	staffType: '_default',
	matched: false,
	unmatchedAdminFallback: false,
	hasOverrides: false
});

// Debug helper - expose to window in browser
if (browser) {
	(window as any).$rolePermissions = rolePermissions;
	(window as any).$debugRolePermissions = () => {
		console.log('rolePermissions:', rolePermissions);
		console.log('userState.user:', userState.user);
		console.log('lookups.staff:', lookups.staff);
		const email = userState.user?.email?.toLowerCase();
		const staffMatch = email
			? Object.values(lookups.staff).find((s) => s.email?.toLowerCase() === email)
			: null;
		console.log('Matched staff:', staffMatch);
		console.log('Config:', _config);
		console.log('Impersonating:', _impersonatedStaff);
	};
}

function recompute() {
	const config = _config ?? {};

	// Storybook/test mock bypasses normal lookup
	if ('_mock' in config && config['_mock']) {
		Object.assign(rolePermissions, config['_mock'], { loading: false });
		return;
	}

	let staffType: string;
	if (_impersonatedStaff) {
		staffType = _impersonatedStaff.staff_type ?? '_default';
	} else {
		const email = userState.user?.email?.toLowerCase();
		staffType = email
			? (Object.values(lookups.staff).find((s) => s.email?.toLowerCase() === email)
					?.staff_type ?? '_default')
			: '_default';
	}

	// isAdmin (lookups/admins) and staff_type (lookups/staff) are separate
	// systems — an admin whose email has no staff_type match must not silently
	// land on the para-level '_default' tier. Logic lives in resolveBaseTier
	// (shared/src/schemas/permissions.ts) so it's unit-testable.
	const { tier: base, unmatchedAdminFallback: isUnmatchedAdmin } = resolveBaseTier({
		staffType,
		config,
		isAdmin: !!userState.isAdmin,
		impersonating: _impersonatedStaff !== null
	});

	// Don't apply the real admin's personal overrides when impersonating
	const overrides = _impersonatedStaff ? undefined : userState.adminDataPermissions;
	Object.assign(rolePermissionsDebug, {
		staffType,
		matched: staffType !== '_default',
		unmatchedAdminFallback: isUnmatchedAdmin,
		hasOverrides: !!overrides
	});
	Object.assign(rolePermissions, overrides ? { ...base, ...overrides } : base, {
		loading: _config === null
	});
}

/**
 * Subscribe to config/rolePermissions and start computing effective permissions.
 * Call from layout onMount, after loadStaff() so staff_type can be resolved.
 */
export function loadRolePermissions() {
	if (!browser || _unsubscribe) return;
	const db = getFirestore();
	const unsubSnapshot = onSnapshot(
		doc(db, 'config', 'rolePermissions'),
		(snap) => {
			_config = snap.exists() ? (snap.data() as RolePermissionsConfig) : {};
			recompute();
		},
		(err) => {
			console.error('[rolePermissions] failed to load config:', err);
			_config = {};
			recompute();
		}
	);
	// recompute() reads lookups.staff and userState, which load asynchronously
	// and can land AFTER the first config snapshot — without this effect the
	// tier silently stuck at '_default' whenever the config won the race
	// (which tier you got depended on network timing).
	const stopEffect = $effect.root(() => {
		$effect(() => {
			recompute(); // tracks everything recompute reads
		});
	});
	_unsubscribe = () => {
		unsubSnapshot();
		stopEffect();
	};
}

/**
 * Override permissions for Storybook/tests only.
 */
export function setMockRolePermissions(perms: Partial<RolePermissions> | null) {
	if (perms === null) {
		_config = null;
		recompute();
		return;
	}
	_config = { _mock: { ...DEFAULT_ROLE_PERMISSIONS['_default'], ...perms } };
	recompute();
}
